// Menu item options management hooks
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/query-client'
import type { MenuItemOption } from '@/types/entities'
import { isAssistantLoggedIn } from '@/lib/permissions'
import { useRestaurantContext } from '@/contexts/RestaurantContext'

/**
 * Fetch all options for a specific menu item
 */
export function useMenuItemOptions(menuItemId: string | null) {
  return useQuery({
    queryKey: ['menu-item-options', menuItemId],
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async (): Promise<MenuItemOption[]> => {
      if (!menuItemId) {
        return []
      }

      const { data, error } = await supabase
        .from('menu_item_options')
        .select('*')
        .eq('menu_item_id', menuItemId)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true })

      if (error) {
        throw error
      }

      return (data || []).map((option: any) => ({
        id: option.id,
        menuItemId: option.menu_item_id,
        nameAr: option.name_ar,
        nameFr: option.name_fr,
        priceModifier: option.price_modifier || 0,
        isDefault: option.is_default || false,
        sortOrder: option.sort_order || 0,
        createdAt: option.created_at || '',
        updatedAt: option.updated_at || '',
      }))
    },
    enabled: !!menuItemId,
  })
}

/**
 * Create new menu item option mutation
 * Supports both admin users and assistants with appropriate permissions
 */
export function useCreateMenuItemOption() {
  const queryClient = useQueryClient()
  const { selectedRestaurantId } = useRestaurantContext()

  return useMutation({
    mutationFn: async (request: {
      menuItemId: string
      nameAr: string
      nameFr: string
      priceModifier?: number
      isDefault?: boolean
      sortOrder?: number
    }) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        if (!selectedRestaurantId) {
          throw new Error('No restaurant selected');
        }
        const { data: optionId, error } = await supabase.rpc('assistant_create_menu_item_option', {
          p_restaurant_id: selectedRestaurantId,
          p_menu_item_id: request.menuItemId,
          p_name_ar: request.nameAr,
          p_name_fr: request.nameFr,
          p_price_modifier: request.priceModifier || 0,
          p_is_default: request.isDefault || false,
          p_sort_order: request.sortOrder || 0,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied')
          }
          throw error
        }

        return optionId
      }

      // Admin user - use existing RPC
      const { data, error } = await (supabase.rpc as any)('create_menu_item_option', {
        p_menu_item_id: request.menuItemId,
        p_name_ar: request.nameAr,
        p_name_fr: request.nameFr,
        p_price_modifier: request.priceModifier || 0,
        p_is_default: request.isDefault || false,
        p_sort_order: request.sortOrder || 0,
      })

      if (error) {
        throw error
      }

      if (data && data.length > 0) {
        const result = data[0]
        if (!result.success) {
          throw new Error(result.message || 'Failed to create option')
        }
        return result.option_id
      }

      throw new Error('No response from server')
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['menu-item-options', variables.menuItemId] })
      queryClient.invalidateQueries({ queryKey: queryKeys.menuItems.all() })
    },
  })
}

/**
 * Update menu item option mutation
 * Supports both admin users and assistants with appropriate permissions
 */
export function useUpdateMenuItemOption(menuItemId: string) {
  const queryClient = useQueryClient()
  const { selectedRestaurantId } = useRestaurantContext()

  return useMutation({
    mutationFn: async (request: {
      optionId: string
      nameAr: string
      nameFr: string
      priceModifier: number
      isDefault: boolean
      sortOrder: number
    }) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        if (!selectedRestaurantId) {
          throw new Error('No restaurant selected');
        }
        const { error } = await supabase.rpc('assistant_update_menu_item_option', {
          p_restaurant_id: selectedRestaurantId,
          p_option_id: request.optionId,
          p_name_ar: request.nameAr,
          p_name_fr: request.nameFr,
          p_price_modifier: request.priceModifier,
          p_is_default: request.isDefault,
          p_sort_order: request.sortOrder,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied')
          }
          throw error
        }

        return true
      }

      // Admin user - use existing RPC
      const { data, error } = await (supabase.rpc as any)('update_menu_item_option', {
        p_option_id: request.optionId,
        p_name_ar: request.nameAr,
        p_name_fr: request.nameFr,
        p_price_modifier: request.priceModifier,
        p_is_default: request.isDefault,
        p_sort_order: request.sortOrder,
      })

      if (error) {
        throw error
      }

      if (data && data.length > 0) {
        const result = data[0]
        if (!result.success) {
          throw new Error(result.message || 'Failed to update option')
        }
      }

      return true
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-item-options', menuItemId] })
      queryClient.invalidateQueries({ queryKey: queryKeys.menuItems.all() })
    },
  })
}

/**
 * Delete menu item option mutation
 * Supports both admin users and assistants with appropriate permissions
 */
export function useDeleteMenuItemOption(menuItemId: string) {
  const queryClient = useQueryClient()
  const { selectedRestaurantId } = useRestaurantContext()

  return useMutation({
    mutationFn: async (optionId: string) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        if (!selectedRestaurantId) {
          throw new Error('No restaurant selected');
        }
        const { error } = await supabase.rpc('assistant_delete_menu_item_option', {
          p_restaurant_id: selectedRestaurantId,
          p_option_id: optionId,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied')
          }
          throw error
        }

        return true
      }

      // Admin user - use existing RPC
      const { data, error } = await (supabase.rpc as any)('delete_menu_item_option', {
        p_option_id: optionId,
      })

      if (error) {
        throw error
      }

      if (data && data.length > 0) {
        const result = data[0]
        if (!result.success) {
          throw new Error(result.message || 'Failed to delete option')
        }
      }

      return true
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-item-options', menuItemId] })
      queryClient.invalidateQueries({ queryKey: queryKeys.menuItems.all() })
    },
  })
}
